/**
 * Loop-class self-service returns portal (Northwind Retail demo).
 * Policy engine → outcome card → exchange offers / label / exception tray.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { adviseDemoAssistantApi, getReturnsCatalogApi } from '../../api/demoAssistants.api';
import ClawDecisionPanel from '../../components/demo/ClawDecisionPanel';
import { useSpeechSynthesis } from '../../hooks/useSpeechSynthesis';

const TEAL = '#0f766e';
const INK = '#0c1f1c';
const REASONS = ['Changed mind', 'Wrong size', 'Defective', 'Not as described', 'Late delivery', 'Other'];
const CONDITIONS = ['Unopened', 'Opened, unused', 'Used, like new', 'Used, noticeable wear', 'Damaged'];

const OUTCOME_TONE = {
  approve_refund: { bg: '#ecfdf5', border: '#6ee7b7', fg: '#065f46', label: 'Refund approved' },
  offer_exchange: { bg: '#eff6ff', border: '#93c5fd', fg: '#1e3a8a', label: 'Exchange available' },
  store_credit: { bg: '#fffbeb', border: '#fcd34d', fg: '#92400e', label: 'Store credit' },
  exception_review: { bg: '#fff7ed', border: '#fdba74', fg: '#9a3412', label: 'Needs review' },
  reject: { bg: '#fef2f2', border: '#fca5a5', fg: '#991b1b', label: 'Not eligible' },
};

function money(n) {
  if (n == null || Number.isNaN(Number(n))) return null;
  return `$${Number(n).toFixed(2)}`;
}

function DemoOrderChip({ order, selected, onSelect }) {
  return (
    <Button
      onClick={() => onSelect(order)}
      variant={selected ? 'contained' : 'outlined'}
      size="small"
      sx={{
        textTransform: 'none',
        borderRadius: 2,
        px: 1.5,
        ...(selected
          ? { bgcolor: TEAL, borderColor: TEAL, '&:hover': { bgcolor: '#0d9488' } }
          : { borderColor: 'rgba(15,118,110,0.35)', color: TEAL }),
      }}
    >
      {order.orderId}
    </Button>
  );
}

function OutcomeHero({ result, selectedResolution, onResolution, selectedExchange, onExchange }) {
  const policy = result?.policy || {};
  const outcome = policy.outcome || policy.status || 'approve_refund';
  const tone = OUTCOME_TONE[outcome] || OUTCOME_TONE.approve_refund;
  const resolutions = result?.resolutions || [];
  const exchanges = result?.exchangeOffers || [];
  const fulfillment = result?.fulfillment || {};
  const item = result?.item || {};
  const { speak, cancel, isSpeaking, hasSupport } = useSpeechSynthesis();
  const speakText = [
    result?.routing?.primaryTrackLabel || tone.label,
    policy.customerMessage || result?.routing?.rationale,
    Array.isArray(result?.routing?.nextSteps) ? result.routing.nextSteps.slice(0, 2).join('. ') : '',
  ].filter(Boolean).join('. ');

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: `1px solid ${tone.border}`,
        bgcolor: tone.bg,
        p: { xs: 2, sm: 2.75 },
      }}
      data-testid="returns-outcome-card"
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
        <Chip
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
          label={tone.label}
          size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.7)', color: tone.fg, fontWeight: 600 }}
        />
        {policy.ruleId ? (
          <Chip
            icon={<PolicyOutlinedIcon sx={{ fontSize: 16 }} />}
            label={`${policy.ruleId} · ${policy.ruleTitle || ''}`}
            size="small"
            variant="outlined"
            sx={{ borderColor: tone.border, color: tone.fg }}
          />
        ) : null}
        {policy.riskBand ? (
          <Chip
            label={`Risk ${policy.riskBand}`}
            size="small"
            sx={{
              bgcolor: policy.riskBand === 'high' ? '#fee2e2' : policy.riskBand === 'medium' ? '#ffedd5' : '#ecfdf5',
              fontWeight: 500,
            }}
          />
        ) : null}
        {hasSupport && (
          <Button
            size="small"
            startIcon={<VolumeUpIcon />}
            onClick={() => (isSpeaking ? cancel() : speak(speakText))}
            sx={{ textTransform: 'none', color: tone.fg }}
          >
            {isSpeaking ? 'Stop' : 'Listen'}
          </Button>
        )}
      </Stack>

      <Typography
        sx={{
          fontFamily: '"Fraunces", "Source Serif 4", Georgia, serif',
          fontWeight: 650,
          fontSize: { xs: '1.35rem', sm: '1.55rem' },
          color: INK,
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          mb: 0.75,
        }}
      >
        {result?.routing?.primaryTrackLabel || tone.label}
      </Typography>
      <Typography sx={{ color: 'rgba(12,31,28,0.78)', mb: 2, maxWidth: 52 * 8 }}>
        {policy.customerMessage || result?.routing?.rationale}
      </Typography>

      {(policy.escalate || policy.human_review_required || result?.claw?.decision?.human_review_required) && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2 }}
          data-testid="returns-human-review-gate"
        >
          Human approval gate — CLAW paused label export until a specialist confirms this exception.
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.65)', borderRadius: 2, p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Item</Typography>
          <Typography fontWeight={600}>{item.name || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[item.sku, item.category, money(item.price)].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.65)', borderRadius: 2, p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Timeline</Typography>
          <Typography fontWeight={600}>{fulfillment.refundEta || '—'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {fulfillment.carrierHint || 'Carrier TBD'}
          </Typography>
        </Box>
      </Stack>

      {resolutions.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: INK }}>Choose how to resolve</Typography>
          <RadioGroup
            value={selectedResolution || ''}
            onChange={(e) => onResolution(e.target.value)}
          >
            <Stack spacing={1}>
              {resolutions.map((r) => (
                <FormControlLabel
                  key={r.id}
                  value={r.id}
                  control={<Radio size="small" sx={{ color: TEAL, '&.Mui-checked': { color: TEAL } }} />}
                  sx={{
                    m: 0,
                    alignItems: 'flex-start',
                    border: '1px solid',
                    borderColor: selectedResolution === r.id ? TEAL : 'rgba(0,0,0,0.08)',
                    bgcolor: selectedResolution === r.id ? 'rgba(15,118,110,0.06)' : '#fff',
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.85,
                  }}
                  label={(
                    <Box sx={{ pt: 0.35 }}>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography fontWeight={600}>{r.label}</Typography>
                        {r.recommended ? (
                          <Chip label="Recommended" size="small" sx={{ height: 20, bgcolor: TEAL, color: '#fff' }} />
                        ) : null}
                        {r.amount != null ? (
                          <Typography variant="body2" color="text.secondary">{money(r.amount)}</Typography>
                        ) : null}
                      </Stack>
                      <Typography variant="body2" color="text.secondary">{r.description}</Typography>
                    </Box>
                  )}
                />
              ))}
            </Stack>
          </RadioGroup>
        </Box>
      )}

      {(selectedResolution === 'exchange' || outcome === 'offer_exchange') && exchanges.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
            <SwapHorizIcon sx={{ color: TEAL, fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ color: INK }}>Pick a replacement</Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {exchanges.map((ex) => {
              const active = selectedExchange === ex.sku;
              return (
                <Button
                  key={ex.sku}
                  onClick={() => onExchange(ex.sku)}
                  variant={active ? 'contained' : 'outlined'}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    textAlign: 'left',
                    minWidth: 160,
                    ...(active
                      ? { bgcolor: TEAL, borderColor: TEAL }
                      : { borderColor: 'rgba(15,118,110,0.3)', color: INK }),
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{ex.name}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                      {ex.sku} · {money(ex.price)}
                      {ex.delta === 0 ? ' · even swap' : ''}
                    </Typography>
                  </Box>
                </Button>
              );
            })}
          </Stack>
        </Box>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap>
        {fulfillment.labelUrl ? (
          <Button
            variant="contained"
            startIcon={<PrintOutlinedIcon />}
            href={fulfillment.labelUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textTransform: 'none',
              bgcolor: TEAL,
              '&:hover': { bgcolor: '#0d9488' },
              borderRadius: 2,
              px: 2.5,
              py: 1.1,
              fontWeight: 600,
            }}
            data-testid="returns-print-label"
          >
            Print prepaid label
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled
            startIcon={<LocalShippingOutlinedIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Label available after approval
          </Button>
        )}
        <Button
          variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 2, borderColor: TEAL, color: TEAL }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Start another return
        </Button>
      </Stack>

      {Array.isArray(result?.routing?.nextSteps) && result.routing.nextSteps.length > 0 && (
        <Box sx={{ mt: 2.25 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Next steps
          </Typography>
          <Stack component="ol" sx={{ m: 0, pl: 2.25 }} spacing={0.35}>
            {result.routing.nextSteps.map((step) => (
              <Typography component="li" key={step} variant="body2" color="text.secondary">
                {step}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      <ClawDecisionPanel
        claw={result?.claw}
        handoffPacket={result?.handoffPacket}
        accent={TEAL}
        defaultOpen
      />
    </Box>
  );
}

function ExceptionTray({ queue, currentException }) {
  const rows = useMemo(() => {
    const seed = Array.isArray(queue) ? queue : [];
    if (!currentException) return seed;
    const already = seed.some((r) => r.orderId === currentException.orderId && r.id === currentException.id);
    return already ? seed : [currentException, ...seed];
  }, [queue, currentException]);

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid rgba(15,118,110,0.18)',
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
      data-testid="returns-exception-tray"
    >
      <Box sx={{
        px: 2,
        py: 1.5,
        background: 'linear-gradient(105deg, rgba(15,118,110,0.12) 0%, #fff 70%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
      }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <SupportAgentIcon sx={{ color: TEAL }} />
          <Box>
            <Typography fontWeight={700} sx={{ color: INK }}>Exception queue</Typography>
            <Typography variant="caption" color="text.secondary">
              Specialist review — Loop-style recommended actions
            </Typography>
          </Box>
        </Stack>
      </Box>
      <Stack divider={<Divider />}>
        {rows.map((row) => (
          <Box key={row.id} sx={{ px: 2, py: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box>
                <Typography fontWeight={600}>{row.orderId}</Typography>
                <Typography variant="body2" color="text.secondary">{row.reason}</Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: TEAL }}>
                  {row.recommendation}
                </Typography>
              </Box>
              <Stack alignItems="flex-end" spacing={0.5}>
                <Chip
                  size="small"
                  label={row.risk}
                  sx={{
                    textTransform: 'capitalize',
                    bgcolor: row.risk === 'high' ? '#fee2e2' : row.risk === 'medium' ? '#ffedd5' : '#ecfdf5',
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {row.ageHours != null ? `${row.ageHours}h open` : 'new'}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        ))}
        {rows.length === 0 && (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography color="text.secondary" variant="body2">Queue is empty.</Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
}

export default function ReturnsPortalPage() {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [mode, setMode] = useState('customer'); // customer | agent
  const [step, setStep] = useState(0); // 0 find · 1 details · 2 outcome
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [sku, setSku] = useState('');
  const [reason, setReason] = useState('Wrong size');
  const [condition, setCondition] = useState('Opened, unused');
  const [hasReceipt, setHasReceipt] = useState('yes');
  const [preferredResolution, setPreferredResolution] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedResolution, setSelectedResolution] = useState('');
  const [selectedExchange, setSelectedExchange] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getReturnsCatalogApi();
        const cat = res?.catalog || null;
        if (!cancelled) setCatalog(cat);
      } catch (err) {
        if (!cancelled) setCatalogError(err?.message || err?.error || 'Could not load catalog');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const brand = catalog?.brand || { name: 'Northwind Retail', tagline: 'Self-service returns', primaryColor: TEAL };
  const orders = catalog?.orders || [];
  const selectedOrder = orders.find((o) => o.orderId === orderId) || null;
  const items = selectedOrder?.items || [];

  const pickDemoOrder = useCallback((order) => {
    setOrderId(order.orderId);
    setEmail(order.email || '');
    setSku(order.items?.[0]?.sku || '');
    setResult(null);
    setStep(1);
    setError(null);
  }, []);

  const submit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const item = items.find((i) => i.sku === sku) || items[0];
      const body = {
        orderId,
        email,
        sku: sku || item?.sku,
        itemName: item?.name,
        category: item?.category,
        price: item?.price,
        reason,
        condition,
        hasReceipt,
        preferredResolution: preferredResolution || undefined,
      };
      const raw = await adviseDemoAssistantApi('returns', body);
      const payload = raw?.result ? raw : raw?.data || raw;
      const adviseResult = payload?.result || payload;
      if (!adviseResult?.policy && !adviseResult?.routing) {
        throw new Error(payload?.error || 'Unexpected advise response');
      }
      setResult(adviseResult);
      const rec = (adviseResult.resolutions || []).find((r) => r.recommended);
      setSelectedResolution(rec?.id || adviseResult.resolutions?.[0]?.id || '');
      setSelectedExchange(adviseResult.exchangeOffers?.[0]?.sku || '');
      setStep(2);
      if (adviseResult.policy?.escalate) setMode('agent');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Advise failed');
    } finally {
      setLoading(false);
    }
  }, [orderId, email, sku, items, reason, condition, hasReceipt, preferredResolution]);

  const currentException = useMemo(() => {
    if (!result?.policy?.escalate) return null;
    return {
      id: `EX-LIVE-${result.order?.orderId || orderId}`,
      orderId: result.order?.orderId || orderId,
      reason: result.policy?.ruleTitle || reason,
      risk: result.policy?.riskBand || 'medium',
      ageHours: 0,
      recommendation: result.policy?.agentHint || 'Review manually',
    };
  }, [result, orderId, reason]);

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#f3f6f5',
        backgroundImage:
          'radial-gradient(ellipse 80% 50% at 10% -10%, rgba(15,118,110,0.14), transparent),'
          + 'radial-gradient(ellipse 60% 40% at 100% 0%, rgba(20,83,45,0.08), transparent)',
      }}
      data-testid="returns-portal"
    >
      <Box
        component="header"
        sx={{
          borderBottom: '1px solid rgba(12,31,28,0.08)',
          bgcolor: 'rgba(255,255,255,0.82)',
          backdropFilter: 'blur(10px)',
          position: 'sticky',
          top: 0,
          zIndex: 4,
        }}
      >
        <Box sx={{
          maxWidth: 1080,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: 1.75,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Fraunces", "Source Serif 4", Georgia, serif',
                fontWeight: 700,
                fontSize: { xs: '1.45rem', sm: '1.75rem' },
                letterSpacing: '-0.03em',
                color: brand.primaryColor || TEAL,
                lineHeight: 1.1,
              }}
            >
              {brand.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {brand.tagline || 'Self-service returns'}
              {' · '}
              <Link component={RouterLink} to="/studio/help/returns" underline="hover" color="inherit">
                How this works
              </Link>
              {' · '}
              <Link component={RouterLink} to="/" underline="hover" color="inherit">
                InterviewFlow
              </Link>
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75}>
            <Button
              size="small"
              variant={mode === 'customer' ? 'contained' : 'outlined'}
              onClick={() => setMode('customer')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                ...(mode === 'customer' ? { bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } } : { color: TEAL, borderColor: TEAL }),
              }}
            >
              Customer portal
            </Button>
            <Button
              size="small"
              variant={mode === 'agent' ? 'contained' : 'outlined'}
              startIcon={<SupportAgentIcon sx={{ fontSize: 18 }} />}
              onClick={() => setMode('agent')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                ...(mode === 'agent' ? { bgcolor: TEAL, '&:hover': { bgcolor: '#0d9488' } } : { color: TEAL, borderColor: TEAL }),
              }}
            >
              Exceptions
            </Button>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1080, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 4 } }}>
        {mode === 'agent' ? (
          <Stack spacing={2.5}>
            <Typography
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 650,
                fontSize: '1.35rem',
                color: INK,
              }}
            >
              Returns operations
            </Typography>
            <ExceptionTray
              queue={result?.exceptionQueue || catalog?.exceptionQueueSeed || []}
              currentException={currentException}
            />
            {result?.policy?.agentHint && (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <strong>Agent hint:</strong> {result.policy.agentHint}
              </Alert>
            )}
            <Button onClick={() => setMode('customer')} sx={{ alignSelf: 'flex-start', textTransform: 'none', color: TEAL }}>
              ← Back to customer portal
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              {['Find order', 'Return details', 'Outcome'].map((label, i) => (
                <Chip
                  key={label}
                  label={`${i + 1}. ${label}`}
                  size="small"
                  onClick={() => { if (i <= step) setStep(i); }}
                  sx={{
                    fontWeight: step === i ? 700 : 500,
                    bgcolor: step === i ? TEAL : step > i ? 'rgba(15,118,110,0.12)' : 'rgba(0,0,0,0.04)',
                    color: step === i ? '#fff' : INK,
                    cursor: i <= step ? 'pointer' : 'default',
                  }}
                />
              ))}
            </Stack>

            {catalogError && (
              <Alert severity="warning">Catalog offline — you can still enter an order manually. ({catalogError})</Alert>
            )}
            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

            {step === 0 && (
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 3,
                  border: '1px solid rgba(12,31,28,0.08)',
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 650,
                    fontSize: '1.4rem',
                    color: INK,
                    mb: 0.5,
                  }}
                >
                  Start a return
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2.5, maxWidth: 480 }}>
                  Look up your order. Demo catalog includes three scenarios — wrong size, device, and hazmat.
                </Typography>

                {orders.length > 0 && (
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Try a demo order
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {orders.map((o) => (
                        <DemoOrderChip
                          key={o.orderId}
                          order={o}
                          selected={orderId === o.orderId}
                          onSelect={pickDemoOrder}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                  <TextField
                    label="Order number"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="ORD-10482"
                  />
                  <TextField
                    label="Email on order"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    size="small"
                    placeholder="alex@example.com"
                  />
                </Stack>
                <Button
                  variant="contained"
                  disabled={!orderId.trim()}
                  onClick={() => {
                    if (selectedOrder?.items?.[0] && !sku) setSku(selectedOrder.items[0].sku);
                    setStep(1);
                  }}
                  sx={{
                    textTransform: 'none',
                    bgcolor: TEAL,
                    borderRadius: 2,
                    px: 2.5,
                    py: 1,
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#0d9488' },
                  }}
                >
                  Continue
                </Button>
              </Box>
            )}

            {step === 1 && (
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: 3,
                  border: '1px solid rgba(12,31,28,0.08)',
                  p: { xs: 2, sm: 3 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 650,
                    fontSize: '1.35rem',
                    color: INK,
                    mb: 2,
                  }}
                >
                  What are you returning?
                </Typography>

                {items.length > 0 ? (
                  <Stack spacing={1} sx={{ mb: 2.5 }}>
                    {items.map((it) => {
                      const active = sku === it.sku;
                      return (
                        <Button
                          key={it.sku}
                          onClick={() => setSku(it.sku)}
                          variant="outlined"
                          sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            borderRadius: 2,
                            borderColor: active ? TEAL : 'rgba(0,0,0,0.12)',
                            bgcolor: active ? 'rgba(15,118,110,0.06)' : '#fff',
                            color: INK,
                            py: 1.25,
                          }}
                        >
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography fontWeight={600}>{it.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {it.sku} · {it.category} · {money(it.price)}
                            </Typography>
                          </Box>
                        </Button>
                      );
                    })}
                  </Stack>
                ) : (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Order not in demo catalog — policy still runs on the details you enter.
                  </Alert>
                )}

                <Stack spacing={1.75} sx={{ maxWidth: 420 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Reason</InputLabel>
                    <Select label="Reason" value={reason} onChange={(e) => setReason(e.target.value)}>
                      {REASONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Condition</InputLabel>
                    <Select label="Condition" value={condition} onChange={(e) => setCondition(e.target.value)}>
                      {CONDITIONS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Have receipt?</InputLabel>
                    <Select label="Have receipt?" value={hasReceipt} onChange={(e) => setHasReceipt(e.target.value)}>
                      <MenuItem value="yes">Yes</MenuItem>
                      <MenuItem value="no">No</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Prefer</InputLabel>
                    <Select
                      label="Prefer"
                      value={preferredResolution}
                      onChange={(e) => setPreferredResolution(e.target.value)}
                    >
                      <MenuItem value="">No preference</MenuItem>
                      <MenuItem value="exchange">Exchange</MenuItem>
                      <MenuItem value="refund">Refund</MenuItem>
                      <MenuItem value="credit">Store credit</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                  <Button onClick={() => setStep(0)} sx={{ textTransform: 'none', color: TEAL }}>Back</Button>
                  <Button
                    variant="contained"
                    disabled={loading || (items.length > 0 && !sku)}
                    onClick={submit}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                      textTransform: 'none',
                      bgcolor: TEAL,
                      borderRadius: 2,
                      px: 2.5,
                      fontWeight: 600,
                      '&:hover': { bgcolor: '#0d9488' },
                    }}
                    data-testid="returns-check-eligibility"
                  >
                    Check eligibility
                  </Button>
                </Stack>
              </Box>
            )}

            {step === 2 && result && (
              <OutcomeHero
                result={result}
                selectedResolution={selectedResolution}
                onResolution={setSelectedResolution}
                selectedExchange={selectedExchange}
                onExchange={setSelectedExchange}
              />
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
